import { MRT_RowData } from "material-react-table";
import {
    Stack,
    useTheme,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Divider,
} from "@mui/material";
import { CustomMRTFilter } from "../mrt.types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { EmptyNullValue } from "../EmptyNullFilter";

export type JsonAttributeFilterValue = {
    value?: string;
    min?: number | string;
    max?: number | string;
    emptyNull?: EmptyNullValue;
};

type JsonAttributeFilterProps<T extends MRT_RowData> = CustomMRTFilter<T> & {
    attributeType?: string;
};

export const JsonAttributeFilter = <T extends MRT_RowData>(props: JsonAttributeFilterProps<T>) => {
    const { attributeType, ...mrtProps } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    const isNumericType = attributeType === "float" || attributeType === "int";

    const initFilterValue = mrtProps.column.getFilterValue() as
        | JsonAttributeFilterValue
        | string
        | EmptyNullValue
        | undefined;

    // Parse initial values
    let initialEmptyNull: string = "all";
    let initialValue: string = "";
    let initialMin: string = "";
    let initialMax: string = "";

    if (typeof initFilterValue === "string") {
        if (initFilterValue === "empty" || initFilterValue === "notEmpty") {
            // EmptyNullValue format
            initialEmptyNull = initFilterValue;
        } else {
            // Plain string value
            initialValue = initFilterValue;
        }
    } else if (initFilterValue && typeof initFilterValue === "object") {
        // JsonAttributeFilterValue format
        initialEmptyNull = initFilterValue.emptyNull || "all";
        initialValue = initFilterValue.value || "";
        initialMin = initFilterValue.min?.toString() || "";
        initialMax = initFilterValue.max?.toString() || "";
    }

    const [emptyNullValue, setEmptyNullValue] = useState<string>(initialEmptyNull);
    const [textValue, setTextValue] = useState<string>(initialValue);
    const [minValue, setMinValue] = useState<string>(initialMin);
    const [maxValue, setMaxValue] = useState<string>(initialMax);

    // Debounce effect to update filter value
    useEffect(() => {
        if (!mrtProps.column) return;

        const timeoutId = setTimeout(() => {
            const hasEmptyNull = emptyNullValue !== "all";

            if (isNumericType) {
                const hasMin = minValue !== "" && !isNaN(Number(minValue));
                const hasMax = maxValue !== "" && !isNaN(Number(maxValue));

                // If no filters are set, clear the filter completely
                if (!hasMin && !hasMax && !hasEmptyNull) {
                    mrtProps.column.setFilterValue(undefined);
                    return;
                }

                // Build filter value
                const filterValue: JsonAttributeFilterValue = {};

                if (hasMin) filterValue.min = Number(minValue);
                if (hasMax) filterValue.max = Number(maxValue);
                if (hasEmptyNull) filterValue.emptyNull = emptyNullValue as EmptyNullValue;

                mrtProps.column.setFilterValue(filterValue);
            } else {
                const hasValue = textValue !== "";

                // If no filters are set, clear the filter completely
                if (!hasEmptyNull && !hasValue) {
                    mrtProps.column.setFilterValue(undefined);
                    return;
                }

                // Build filter value
                const filterValue: JsonAttributeFilterValue = {};

                if (hasValue) filterValue.value = textValue;
                if (hasEmptyNull) filterValue.emptyNull = emptyNullValue as EmptyNullValue;

                mrtProps.column.setFilterValue(filterValue);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [textValue, minValue, maxValue, emptyNullValue, mrtProps.column, isNumericType]);

    return (
        <Stack gap={theme.spacing(2)} sx={{ padding: theme.spacing(1), minWidth: "200px" }}>
            {/* Empty/Not Empty Filter */}
            <FormControl component="fieldset" size="small">
                <FormLabel component="legend" sx={{ fontSize: "0.875rem", marginBottom: theme.spacing(0.5) }}>
                    {t("grid.filter.jsonAttribute.emptyLabel", "État")}
                </FormLabel>
                <RadioGroup
                    value={emptyNullValue}
                    onChange={(e) => setEmptyNullValue(e.target.value)}
                    aria-label={t("grid.filter.emptyNull.ariaLabel", "Filter by empty or not empty")}
                >
                    <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.all", "Tous")}
                    />
                    <FormControlLabel
                        value="empty"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.empty", "Vide")}
                    />
                    <FormControlLabel
                        value="notEmpty"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.notEmpty", "Non vide")}
                    />
                </RadioGroup>
            </FormControl>

            <Divider />

            {/* Value Filter - Show different UI based on attribute type */}
            {isNumericType ?
                <Stack gap={theme.spacing(1)}>
                    <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
                        {t("grid.filter.jsonAttribute.rangeLabel", "Plage de valeurs")}
                    </FormLabel>
                    <TextField
                        label={t("grid.filter.range.min", "Min")}
                        type="number"
                        value={minValue}
                        onChange={(e) => setMinValue(e.target.value)}
                        variant="outlined"
                        size="small"
                        inputProps={{
                            step: attributeType === "float" ? "any" : "1",
                        }}
                    />
                    <TextField
                        label={t("grid.filter.range.max", "Max")}
                        type="number"
                        value={maxValue}
                        onChange={(e) => setMaxValue(e.target.value)}
                        variant="outlined"
                        size="small"
                        inputProps={{
                            step: attributeType === "float" ? "any" : "1",
                        }}
                    />
                </Stack>
            :   <Stack gap={theme.spacing(1)}>
                    <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
                        {t("grid.filter.jsonAttribute.valueLabel", "Valeur")}
                    </FormLabel>
                    <TextField
                        label={t("grid.filter.jsonAttribute.valuePlaceholder", "Rechercher...")}
                        type="text"
                        value={textValue}
                        onChange={(e) => setTextValue(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </Stack>
            }
        </Stack>
    );
};
